import type { WizardFormData } from '../page';

interface Props {
  formData: WizardFormData;
  onChange: (updates: Partial<WizardFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  error?: string;
}

interface ToggleItemProps {
  label: string;
  description: string;
  icon: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const ToggleItem = ({ label, description, icon, checked, onChange }: ToggleItemProps) => (
  <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
      <i className={`${icon} text-emerald-600 text-lg`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-600 text-gray-900">{label}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${checked ? 'bg-emerald-500' : 'bg-gray-300'}`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all duration-200 ${checked ? 'left-6' : 'left-0.5'}`}
      />
    </button>
  </div>
);

export const WizardStep4 = ({ formData, onChange, onNext, onBack, loading = false, error }: Props) => {
  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-700 text-gray-900 mb-1">Configuração inicial</h2>
        <p className="text-gray-500 text-sm">Nos diga um pouco sobre o que você já tem. Pode configurar depois também!</p>
      </div>

      {error && (
        <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5">
          <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <div className="space-y-3">
        <ToggleItem
          label="Tenho conta bancária"
          description="Conta corrente, poupança, digital ou carteira"
          icon="ri-bank-line"
          checked={formData.hasBankAccount}
          onChange={(v) => onChange({ hasBankAccount: v })}
        />
        <ToggleItem
          label="Tenho cartão de crédito"
          description="Cartão físico ou virtual com fatura mensal"
          icon="ri-bank-card-line"
          checked={formData.hasCreditCard}
          onChange={(v) => onChange({ hasCreditCard: v })}
        />
        {(formData.hasBankAccount || formData.hasCreditCard) && (
          <ToggleItem
            label="Quero cadastrar agora"
            description="Configurar minha conta/cartão já no próximo passo"
            icon="ri-add-circle-line"
            checked={formData.setupNow}
            onChange={(v) => onChange({ setupNow: v })}
          />
        )}
      </div>

      {!formData.hasBankAccount && !formData.hasCreditCard && (
        <div className="mt-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
          <p className="text-sm text-emerald-700 font-500">
            <i className="ri-information-line mr-1.5" />
            Sem problema! Você pode adicionar suas contas e cartões mais tarde no painel.
          </p>
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="h-12 px-6 border border-gray-200 text-gray-700 font-600 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          Voltar
        </button>
        <button
          onClick={onNext}
          disabled={loading}
          className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-70"
        >
          {loading ? (
            <><i className="ri-loader-4-line animate-spin text-base" />Salvando...</>
          ) : (
            <>Continuar<i className="ri-arrow-right-line text-base" /></>
          )}
        </button>
      </div>
      <button
        onClick={onNext}
        disabled={loading}
        className="mt-3 w-full text-sm text-gray-400 hover:text-gray-600 cursor-pointer text-center transition-colors disabled:opacity-50"
      >
        Configurar depois →
      </button>
    </div>
  );
};
